import { useEffect, useState } from "react";
import { ChatBubble } from "./flowChatBubbles";
import FlowAssertPicker from "./flowAssertPicker";
import FlowAssertParamEditor from "./flowAssertParamEditor";

export default function FlowChatEditor({
    caseItem,
    caseIndex,
    asserts = [],
    onUpdateTurn,
    onToggleAssert,
    onUpdateAssertParam,
    onAddTurn,
    onRemoveTurn,
    onMoveTurn,
    onToggleBurst,
    onAddDebouncePreset,
    onUpdateTags,
}) {
    const turns = Array.isArray(caseItem?.turns) ? caseItem.turns : [];
    const [selectedTurnIndex, setSelectedTurnIndex] = useState(null);

    useEffect(() => {
        if (selectedTurnIndex == null) return;
        if (turns.length === 0) {
            setSelectedTurnIndex(null);
            return;
        }
        if (selectedTurnIndex >= turns.length) {
            setSelectedTurnIndex(turns.length - 1);
        }
    }, [turns.length, selectedTurnIndex]);

    const selectedTurn =
        selectedTurnIndex != null && turns[selectedTurnIndex]
            ? turns[selectedTurnIndex]
            : null;
    const turnCount = turns.length;

    return (
        <div className="space-y-3" aria-label="Editor chat flow">
            <p className="text-[11px] text-gray-400 leading-snug">
                Monte a conversa como no WhatsApp. Clique num balão para editar mensagem,
                burst e asserts. Tags ajudam a filtrar cenários na biblioteca.
            </p>
            <div>
                <label className="block text-xs text-gray-300 mb-0.5" htmlFor="flow-tags">
                    Tags do cenário
                </label>
                <p className="text-[10px] text-gray-500 mb-1">
                    Separadas por vírgula (ex.: triagem, escalacao, debounce).
                </p>
                <input
                    id="flow-tags"
                    aria-label="Tags do cenário"
                    value={(caseItem?.tags || []).join(", ")}
                    onChange={(e) =>
                        onUpdateTags?.(
                            caseIndex,
                            e.target.value
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean)
                        )
                    }
                    placeholder="triagem, escalacao"
                    className="text-black p-2 rounded text-sm w-full"
                />
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    aria-label="Adicionar turno"
                    onClick={() => onAddTurn(caseIndex)}
                    className="rounded bg-gray-700 hover:bg-gray-600 px-3 py-1 text-sm text-white"
                >
                    Adicionar turno
                </button>
                <button
                    type="button"
                    aria-label="Preset debounce"
                    onClick={() => onAddDebouncePreset(caseIndex)}
                    className="rounded bg-gray-700 hover:bg-gray-600 px-3 py-1 text-sm text-white"
                >
                    Preset debounce
                </button>
                <button
                    type="button"
                    aria-label="Mover turno para cima"
                    onClick={() => onMoveTurn(caseIndex, selectedTurnIndex, -1)}
                    disabled={selectedTurnIndex == null || selectedTurnIndex <= 0}
                    className="rounded border border-gray-600 px-2 py-1 text-sm text-gray-200 disabled:opacity-40"
                >
                    ↑
                </button>
                <button
                    type="button"
                    aria-label="Mover turno para baixo"
                    onClick={() => onMoveTurn(caseIndex, selectedTurnIndex, 1)}
                    disabled={
                        selectedTurnIndex == null || selectedTurnIndex >= turnCount - 1
                    }
                    className="rounded border border-gray-600 px-2 py-1 text-sm text-gray-200 disabled:opacity-40"
                >
                    ↓
                </button>
                <button
                    type="button"
                    aria-label="Remover turno"
                    onClick={() => onRemoveTurn(caseIndex, selectedTurnIndex)}
                    disabled={selectedTurnIndex == null || turnCount <= 1}
                    className="rounded bg-red-500/80 hover:bg-red-600 px-2 py-1 text-sm text-white disabled:opacity-40"
                >
                    Remover turno
                </button>
            </div>

            <div className="md:flex md:gap-4 md:items-start">
                <div className="flex-1 min-w-0">
                    {turns.map((turn, turnIndex) => (
                        <div key={turn.id || turnIndex}>
                            <ChatBubble
                                role="user"
                                text={turn.mensagem}
                                selected={selectedTurnIndex === turnIndex}
                                onClick={() => setSelectedTurnIndex(turnIndex)}
                                testId={`editor-user-${turn.id || turnIndex}`}
                            />
                            <ChatBubble
                                role="pending"
                                text="resposta do bot (na run)"
                                testId={`editor-pending-${turn.id || turnIndex}`}
                            />
                        </div>
                    ))}
                </div>

                {selectedTurn ? (
                    <aside
                        className="mt-3 md:mt-0 w-full md:w-80 shrink-0 rounded border border-gray-700 bg-gray-800/50 p-3 space-y-3"
                        aria-label="Detalhe do turno"
                    >
                        <div>
                            <p className="text-xs text-gray-300 font-medium mb-0.5">
                                Mensagem do cliente
                            </p>
                            <p className="text-[11px] text-gray-500 mb-1.5 leading-snug">
                                Texto enviado neste turno (como no WhatsApp). Edite e salve a
                                suíte.
                            </p>
                            <textarea
                                aria-label="Mensagem do turno"
                                value={selectedTurn.mensagem || ""}
                                onChange={(e) =>
                                    onUpdateTurn(
                                        caseIndex,
                                        selectedTurnIndex,
                                        "mensagem",
                                        e.target.value
                                    )
                                }
                                rows={3}
                                placeholder="Ex.: como funciona o grading?"
                                className="w-full text-black p-2 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(selectedTurn.burst)}
                                    onChange={() =>
                                        onToggleBurst(caseIndex, selectedTurnIndex)
                                    }
                                    className="h-3 w-3 accent-[#d59106]"
                                />
                                Parte de rajada (burst)
                            </label>
                            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug pl-5">
                                Marque msgs enviadas em sequência rápida. O assert “Debounce
                                agrupou” costuma ir só na última da rajada.
                            </p>
                        </div>
                        <FlowAssertPicker
                            asserts={asserts}
                            selectedIds={selectedTurn.assertIds || []}
                            onToggle={(assertId) =>
                                onToggleAssert(caseIndex, selectedTurnIndex, assertId)
                            }
                        />
                        <FlowAssertParamEditor
                            assertIds={selectedTurn.assertIds || []}
                            assertParams={selectedTurn.assertParams || {}}
                            onChange={(key, value) =>
                                onUpdateAssertParam(
                                    caseIndex,
                                    selectedTurnIndex,
                                    key,
                                    value
                                )
                            }
                        />
                    </aside>
                ) : (
                    <aside
                        className="mt-3 md:mt-0 w-full md:w-80 shrink-0 rounded border border-dashed border-gray-700 bg-gray-900/30 p-3"
                        aria-label="Dica do editor"
                    >
                        <p className="text-xs text-gray-300 font-medium mb-1">
                            Como editar
                        </p>
                        <ul className="text-[11px] text-gray-500 space-y-1 list-disc pl-4 leading-snug">
                            <li>Clique num balão laranja para abrir o detalhe do turno.</li>
                            <li>Use atalhos de assert só nos turnos-chave (FAQ, escala…).</li>
                            <li>Turnos intermediários podem ficar sem assert.</li>
                            <li>Salve a suíte antes de executar.</li>
                        </ul>
                    </aside>
                )}
            </div>
        </div>
    );
}
